import React, { useState, useRef } from "react";
import { graphql } from "gatsby";
import Img from "gatsby-image";
import Footer from "~components/footer";
import { TextHeader } from "~components/header";
import Unity, { UnityContext } from "react-unity-webgl";
import { Section, SubSection } from "~components/text-helpers";
import PRIOR from "~icons/prior-wheel.svg";
import TAP_ICON from "~icons/white-press.svg";
import color from "~styles/color";
import { css } from "@emotion/react";
import { Card, Checkbox, Button } from "antd";
import { FilterOutlined, PushpinFilled } from "@ant-design/icons";
import LEFT_CLICK_ICON from "~icons/left-click.svg";
import ROTATE_MOUSE_ICON from "~icons/rotate-mouse.svg";

// returns a random iTHOR scene
function getRandomScene() {
  // ithor scene
  if (Math.random() < 0.7) {
    let scene = (Math.floor(Math.random() * 4) + 1) * 100;
    if (scene === 100) {
      scene -= 100;
    }
    scene += Math.floor(Math.random() * 30) + 1;
    return `FloorPlan${scene}_physics`;
  }

  // robothor scene
  if (Math.random() < 0.7) {
    // train
    const i = Math.floor(Math.random() * 12) + 1;
    const j = Math.floor(Math.random() * 5) + 1;
    return `FloorPlan_Train${i}_${j}`;
  }

  // robothor val
  const i = Math.floor(Math.random() * 3) + 1;
  const j = Math.floor(Math.random() * 5) + 1;
  return `FloorPlan_Val${i}_${j}`;
}

function getNextScene(sceneName: string) {
  // RoboTHOR
  if (sceneName.includes("Train") || sceneName.includes("Val")) {
    if (sceneName.endsWith("5")) {
      // end of scene group
      if (sceneName.includes("Train")) {
        if (sceneName.endsWith("12_5")) {
          return "FloorPlan_Val1_1";
        }

        let wallNum = parseInt(
          sceneName.substring(
            "FloorPlan_Train".length,
            sceneName.lastIndexOf("_")
          )
        );
        return `FloorPlan_Train${wallNum + 1}_1`;
      }

      if (sceneName.includes("Val")) {
        if (sceneName.endsWith("3_5")) {
          return "FloorPlan1";
        }

        let wallNum = parseInt(
          sceneName.substring(
            "FloorPlan_Val".length,
            sceneName.lastIndexOf("_")
          )
        );
        return `FloorPlan_Val${wallNum + 1}_1`;
      }
    }

    let sceneNum = parseInt(sceneName.substring(sceneName.length - 1));
    return `${sceneName.substring(0, sceneName.length - 1)}${sceneNum + 1}`;
  }

  // remove _physics for now
  sceneName = sceneName.substring(0, sceneName.length - "_physics".length);

  // iTHOR
  if (sceneName.endsWith("30")) {
    // end of scene group
    if (sceneName.endsWith("430")) {
      return "FloorPlan_Train1_1";
    } else if (sceneName.endsWith("330")) {
      return "FloorPlan401_physics";
    } else if (sceneName.endsWith("230")) {
      return "FloorPlan301_physics";
    } else if (sceneName.endsWith("n30")) {
      return "FloorPlan201_physics";
    }
  }

  let sceneNum = parseInt(sceneName.substring("FloorPlan".length));
  return `FloorPlan${sceneNum + 1}_physics`;
}

class Demo extends React.Component<
  { scene: string; setScene: any },
  {
    progress: number;
    isInDemo: boolean;
    changedColors: boolean;
    changedMaterials: boolean;
  }
> {
  unityContent: any;
  unityCanvasStyle: string;
  gameRef: any;
  handleKeyDown: any;

  constructor(props: { scene: string; setScene: any }) {
    super(props);
    this.state = {
      progress: 0,
      isInDemo: false,
      changedColors: false,
      changedMaterials: false,
    };

    this.unityCanvasStyle = "width: 100%; height: 100%; cursor: default;";
    this.unityContent = new UnityContext({
      loaderUrl: "//d15lmnn5du4ok1.cloudfront.net/full/Build/thor-local-WebGL.loader.js",
      dataUrl: "//d15lmnn5du4ok1.cloudfront.net/full/Build/thor-local-WebGL.data",
      frameworkUrl: "//d15lmnn5du4ok1.cloudfront.net/full/Build/thor-local-WebGL.framework.js",
      codeUrl: "//d15lmnn5du4ok1.cloudfront.net/full/Build/thor-local-WebGL.wasm",
      matchWebGLToCanvasSize: true,
      devicePixelRatio: 1,
      });

    this.unityContent.on("progress", (progress: number) => {
      this.setState({
        progress: progress,
      });
      if (progress === 1 && this.props.scene !== "FloorPlan1_physics") {
        this.unityContent.send(
          "PhysicsSceneManager",
          "SwitchScene",
          this.props.scene
        );
      }
    });

    this.gameRef = React.createRef();

    const self = this;
    this.handleKeyDown = (event: any) => {
      // prevents prematurely sending messages
      if (self.state.progress !== 1) {
        return;
      }
      if (event.key === "Escape" || event.key === "q") {
        this.unityContent.send("FPSController", "DisableMouseControl");
        self.setState({ isInDemo: false });
      }
    };
  }

  componentDidUpdate(prevProps: any) {
    if (this.state.progress === 1 && this.props.scene != prevProps.scene) {
      if (this.state.changedColors) {
        this.unityContent.send(
          "FPSController",
          "Step",
          JSON.stringify({
            action: "ResetColors",
          })
        );
      }
      if (this.state.changedMaterials) {
        this.unityContent.send(
          "FPSController",
          "Step",
          JSON.stringify({
            action: "ResetMaterials",
          })
        );
      }
      this.unityContent.send(
        "PhysicsSceneManager",
        "SwitchScene",
        this.props.scene
      );
      this.setState({ changedColors: false, changedMaterials: false });
    }
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  render() {
    return (
      <>
        <div
          css={css`
            position: relative;
            background-color: ${this.state.progress === 1
              ? "transparent"
              : "#231f20"};
            padding-top: 50%;
            overflow-y: hidden;
          `}
        >
          <div
            css={css`
              margin-top: -50%;
            `}
          >
            <div
              onClick={() => {
                if (this.state.progress === 1 && !this.state.isInDemo) {
                  this.unityContent.send("FPSController", "EnableMouseControl");
                  document.body.requestPointerLock();
                  this.setState({ isInDemo: true });
                }
              }}
              css={css`
                user-select: none;
                &:hover {
                  cursor: ${this.state.progress === 1 ? "pointer" : "default"};
                }
              `}
            >
              <div
                css={css`
                  pointer-events: none;

                  /* from unityContent */
                  > div:nth-of-type(1) {
                    background: none !important;
                  }
                `}
                ref={this.gameRef}
              >
                <Unity unityContext={this.unityContent} style={{width: "100%"}}/>;
                <div
                  css={css`
                    position: absolute;
                    left: 50%;
                    bottom: 25px;
                    transform: translateX(-50%);
                    padding: 5px 15px;
                    padding-bottom: 0px;
                    margin-bottom: 0px;
                    border-radius: 5px;
                    transition-duration: 0.3s;
                    opacity: ${this.state.progress === 1 && this.state.isInDemo
                      ? "1"
                      : "0"};
                    font-weight: 600;
                    font-size: 18px;
                    color: white;
                    text-align: center;
                    filter: drop-shadow(0px 0px 5px ${color.gray9});
                  `}
                >
                  Press <KeyboardKey keyboardKey="q" /> to Exit
                </div>
                <div
                  css={css`
                    position: absolute;
                    left: 50%;
                    bottom: 15px;
                    transform: translateX(-50%);
                    padding: 5px 15px;
                    border-radius: 5px;
                    font-weight: 600;
                    transition-duration: 0.3s;
                    font-size: 18px;
                    height: 110px;
                    // keep this check here, as opposed to outside of the component
                    // so it shows loads immediately
                    opacity: ${this.state.progress === 1 && !this.state.isInDemo
                      ? "1"
                      : "0"};
                    color: white;
                    text-align: center;
                    filter: drop-shadow(0px 0px 5px ${color.gray9});
                  `}
                >
                  <img
                    src={TAP_ICON}
                    css={css`
                      width: 50px;
                      margin-top: 10px;
                      margin-bottom: 10px;

                      animation: scaleTap 2s infinite;
                      @keyframes scaleTap {
                        0% {
                          transform: scale(1);
                        }
                        50% {
                          transform: scale(1.15);
                        }
                        100% {
                          transform: scale(1);
                        }
                      }
                    `}
                  />
                  <br />
                  Press to Play
                </div>
                <div
                  css={css`
                    position: absolute;
                    top: 0px;
                    left: 0px;
                    right: 0px;
                    bottom: 0px;
                    background: ${this.state.progress < 1
                      ? "#231f20"
                      : "transparent"};
                    display: block;
                    transition-duration: 3s;
                    transition-timing-function: step-end;
                  `}
                />
                <div
                  css={css`
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    text-align: center;
                    opacity: ${this.state.progress < 1 ? "1" : "0"};
                    transition-duration: 3s;
                  `}
                >
                  <img
                    src={PRIOR}
                    css={css`
                      width: 175px;
                      @media (max-width: 1000px) {
                        width: 100px;
                      }
                      @media (max-width: 500px) {
                        width: 60px;
                      }
                      animation: rotateWheel 5s infinite
                        cubic-bezier(0, 1, 1, 0);
                      @keyframes rotateWheel {
                        0% {
                          transform: rotate(0deg);
                        }
                        50% {
                          transform: rotate(180deg);
                        }
                        100% {
                          transform: rotate(359.9deg);
                        }
                      }
                    `}
                  />
                  <div
                    css={css`
                      width: 50vw;
                      height: 15px;
                      background-color: ${color.gray9};
                      margin-top: 125px;
                      @media (max-width: 1200px) {
                        margin-top: 50px;
                      }
                      @media (max-width: 500px) {
                        margin-top: 30px;
                      }
                      border-radius: 15px;
                      text-align: left;
                      overflow: hidden;
                      position: relative;
                    `}
                  >
                    <div
                      css={css`
                        position: absolute;
                        top: 0px;
                        bottom: 0px;
                        left: 0px;
                        right: calc(100% - ${this.state.progress * 100}%);
                        background-color: ${color.gray1};
                        border-radius: 15px;
                      `}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          css={css`
            user-select: none;
            text-align: center;
            margin-left: 45px;
            margin-right: 45px;
            margin-top: 15px;
            > button {
              margin: 10px;
            }
          `}
        >
          <Button
            type="primary"
            onClick={() => {
              this.props.setScene(getRandomScene());
            }}
          >
            Random Scene
          </Button>
          <Button
            type="primary"
            onClick={() => {
              this.props.setScene((scene) => getNextScene(scene));
            }}
          >
            Next Scene
          </Button>
          <br />
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.setState({ changedMaterials: true });
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "RandomizeMaterials",
                })
              );
            }}
          >
            RandomizeMaterials
          </Button>
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.setState({ changedMaterials: false });
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "ResetMaterials",
                })
              );
            }}
          >
            ResetMaterials
          </Button>
          <br />
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "RandomizeLighting",
                })
              );
            }}
          >
            RandomizeLighting
          </Button>
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "ResetLighting",
                })
              );
            }}
          >
            ResetLighting
          </Button>
          <br />
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.setState({ changedColors: true });
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "RandomizeColors",
                })
              );
            }}
          >
            RandomizeColors
          </Button>
          <Button
            type="primary"
            onClick={() => {
              if (this.state.progress !== 1) {
                return;
              }
              this.setState({ changedColors: false });
              this.unityContent.send(
                "FPSController",
                "Step",
                JSON.stringify({
                  action: "ResetColors",
                })
              );
            }}
          >
            ResetColors
          </Button>
          <br />
          {/* RoboTHOR doesn't support initial random spawn */}
          {this.props.scene.includes("Train") ||
          this.props.scene.includes("Val") ? (
            <></>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() => {
                  if (this.state.progress !== 1) {
                    return;
                  }
                  this.unityContent.send(
                    "FPSController",
                    "Step",
                    JSON.stringify({
                      action: "InitialRandomSpawn",
                    })
                  );
                }}
              >
                InitialRandomSpawn
              </Button>
            </>
          )}
        </div>
      </>
    );
  }
}

function SceneCard(props: {
  onClick: any;
  sceneKey: string;
  data: any;
  selectedScene: string;
}) {
  return (
    <Card
      css={css`
        width: 225px;
        display: inline-block;
        margin: 15px !important;
        margin-left: 0px !important;
        margin-right: 30px !important;
        box-shadow: 0px 10px 25px 0px
          ${props.sceneKey == props.selectedScene ? color.gray1 : color.gray5};
        opacity: ${props.sceneKey == props.selectedScene ? 0.5 : 1};
        transition-duration: 0.3s;
        text-align: center;
        ${props.sceneKey != props.selectedScene
          ? css`
              &:hover {
                box-shadow: 0px 10px 30px 0px ${color.gray6};
                cursor: pointer;
              }
            `
          : ""}
      `}
      cover={<Img fluid={props.data[props.sceneKey].childImageSharp.fluid} />}
      onClick={() => {
        if (props.sceneKey != props.selectedScene) {
          props.onClick(props.sceneKey);
        }
      }}
    >
      <div
        css={css`
          margin-top: -10px;
          margin-bottom: -10px;
          font-size: 16px;
        `}
      >
        {props.sceneKey.includes("_physics")
          ? props.sceneKey.substring(
              0,
              props.sceneKey.length - "_physics".length
            )
          : props.sceneKey}
      </div>
    </Card>
  );
}

function KeyboardKey(props: {
  keyboardKey: string;
  width?: number;
  isPressed?: boolean;
}) {
  return (
    <div
      css={css`
        width: ${props.width ? `calc(45px * ${props.width})` : "45px"};
        height: 45px;
        line-height: 40px;
        border-radius: 5px;
        font-weight: 600;
        color: ${color.gray10};
        box-shadow: inset 0px -5px 6px ${props.isPressed ? color.gray7 : color.gray6};
        background-color: ${props.isPressed ? color.gray5 : color.gray3};
        text-align: center;
        display: inline-block;
      `}
    >
      {props.keyboardKey}
    </div>
  );
}

function Scenes(props: {
  onClick: any;
  scenes: string[];
  data: any;
  selectedScene: string;
}) {
  return (
    <>
      {props.scenes.map((sceneKey: string) => (
        <SceneCard
          sceneKey={sceneKey}
          onClick={props.onClick}
          data={props.data}
          selectedScene={props.selectedScene}
        />
      ))}
    </>
  );
}

function Control(props: {
  action: string;
  details: React.ReactNode;
  keys: React.ReactNode;
}) {
  return (
    <div
      css={css`
        display: inline-block;
        vertical-align: top;
        margin-right: 15px;
        margin-bottom: 15px;
      `}
    >
      <div
        css={css`
          font-weight: 600;
          text-align: center;
          margin-bottom: 8px;
        `}
      >
        {props.action}
      </div>
      <Card
        css={css`
          background-color: ${color.gray2};
          border: 1px solid ${color.gray5};
        `}
        cover={
          <div
            css={css`
              padding: 15px;
            `}
          >
            {props.keys}
          </div>
        }
      >
        <div
          css={css`
            margin: -9px;
            margin-top: -24px;
          `}
        >
          {props.details}
        </div>
      </Card>
    </div>
  );
}

export default function DemoPage({ data, location }) {
  const [scene, setScene] = useState("FloorPlan1_physics"),
    [includeiTHOR, setIncludeiTHOR] = useState(true),
    [includeRoboTHOR, setIncludeRoboTHOR] = useState(true),
    [includeRoboTHORTrain, setIncludeRoboTHORTrain] = useState(true),
    [includeRoboTHORVal, setIncludeRoboTHORVal] = useState(true),
    [includeKitchens, setIncludeKitchens] = useState(true),
    [includeLivingRooms, setIncludeLivingRooms] = useState(true),
    [includeBedrooms, setIncludeBedrooms] = useState(true),
    [includeBathrooms, setIncludeBathrooms] = useState(true);

  let kitchens = [];
  let living_rooms = [];
  let bedrooms = [];
  let bathrooms = [];
  for (let i = 1; i < 31; i++) {
    kitchens.push(`FloorPlan${i}_physics`);
    living_rooms.push(`FloorPlan${200 + i}_physics`);
    bedrooms.push(`FloorPlan${300 + i}_physics`);
    bathrooms.push(`FloorPlan${400 + i}_physics`);
  }

  let robothor_train = [];
  let robothor_val = [];
  for (let i = 1; i < 13; i++) {
    for (let j = 1; j < 6; j++) {
      robothor_train.push(`FloorPlan_Train${i}_${j}`);
    }
  }
  for (let i = 1; i < 4; i++) {
    for (let j = 1; j < 6; j++) {
      robothor_val.push(`FloorPlan_Val${i}_${j}`);
    }
  }

  const sceneClick = (sceneKey) => {
    setScene(sceneKey);

    // window is not present during gatsby build
    if (typeof window !== "undefined") {
      window.scrollTo(0, 175);
    }
  };

  return (
    <>
      <TextHeader
        title="Demo"
        keywords="Demo, AI2-THOR, iTHOR, Interaction, RoboTHOR"
        description="Interactive Demo for the AI2-THOR Framework."
      />
      <div
        css={css`
          max-width: calc(200vh + 256px);
          margin: auto;
        `}
      >
        <div
          css={css`
            width: 256px;
            display: inline-block;
            vertical-align: top;
            position: sticky;
            top: 0px;
            padding: 15px;
            border-right: 1px solid ${color.gray5};
            height: 100vh;
            overflow-y: auto;
            overflow-x: hidden;
            color: ${color.light.blue8};
            background-color: ${color.gray3};
            @media (max-width: 800px) {
              display: none;
            }
          `}
        >
          <div
            css={css`
              font-size: 16px;
              > div {
                margin-top: 5px;
              }
              div {
                margin-top: 3px;
              }
            `}
          >
            <div
              css={css`
                font-weight: 600;
                margin-bottom: 10px !important;
              `}
            >
              <PushpinFilled style={{ marginRight: "5px" }} />
              Current Scene:{" "}
              <span
                css={css`
                  font-weight: 400;
                `}
              >
                {scene.endsWith("_physics")
                  ? scene.substring(0, scene.length - "_physics".length)
                  : scene}
              </span>
            </div>
            <Img fluid={data[scene].childImageSharp.fluid} />
            <hr
              css={css`
                margin-top: 30px;
                margin-bottom: 20px;
                opacity: 0.5;
              `}
            />
            <div
              css={css`
                font-weight: 600;
              `}
            >
              <FilterOutlined style={{ marginRight: "5px" }} />
              Filter Scenes
            </div>
            <div
              css={css`
                > div:not(:nth-of-type(1)) {
                  margin-left: 20px;
                }
                > div {
                  transition-duration: 0.3s;
                  &:hover {
                    cursor: pointer;
                    color: ${color.light.blue4};
                  }
                }
                .emoji-mart-emoji {
                  vertical-align: middle;
                }
              `}
            >
              <div>
                <Checkbox
                  checked={includeiTHOR}
                  onChange={(e: any) => setIncludeiTHOR(e.target.checked)}
                >
                  iTHOR
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={includeKitchens}
                  onChange={(e: any) => setIncludeKitchens(e.target.checked)}
                  disabled={!includeiTHOR}
                >
                  Kitchens
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={includeLivingRooms}
                  onChange={(e: any) => setIncludeLivingRooms(e.target.checked)}
                  disabled={!includeiTHOR}
                >
                  Living Rooms
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={includeBedrooms}
                  onChange={(e: any) => setIncludeBedrooms(e.target.checked)}
                  disabled={!includeiTHOR}
                >
                  Bedrooms
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  defaultChecked={true}
                  onChange={(e: any) => setIncludeBathrooms(e.target.checked)}
                  disabled={!includeiTHOR}
                >
                  Bathrooms
                </Checkbox>
              </div>
            </div>
            <div
              css={css`
                margin-top: 10px !important;
                > div:not(:nth-of-type(1)) {
                  margin-left: 20px;
                }
                > div {
                  transition-duration: 0.3s;
                  &:hover {
                    cursor: pointer;
                    color: ${color.light.blue4};
                  }
                }
              `}
            >
              <div>
                <Checkbox
                  checked={includeRoboTHOR}
                  onChange={(e: any) => setIncludeRoboTHOR(e.target.checked)}
                >
                  RoboTHOR
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={includeRoboTHORTrain}
                  onChange={(e: any) =>
                    setIncludeRoboTHORTrain(e.target.checked)
                  }
                  disabled={!includeRoboTHOR}
                >
                  Training
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={includeRoboTHORVal}
                  onChange={(e: any) => setIncludeRoboTHORVal(e.target.checked)}
                  disabled={!includeRoboTHOR}
                >
                  Validation
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
        <div
          css={css`
            position: relative;
            width: calc(100% - 256px);
            margin: auto;
            display: inline-block;
            @media (max-width: 800px) {
              width: 100%;
            }
          `}
        >
          <Demo scene={scene} setScene={setScene} />
          <div
            css={css`
              margin-top: 25px;
              padding-left: 45px;
              padding-right: 45px;
              @media (max-width: 800px) {
                padding-left: 30px;
                padding-right: 30px;
              }
            `}
          >
            <Section sectionTitle="Controls" emoji="joystick">
              <Control
                keys={
                  <>
                    <div
                      css={css`
                        text-align: center;
                        width: 125px;
                      `}
                    >
                      <KeyboardKey keyboardKey="q" />
                    </div>
                  </>
                }
                action="Pause the Demo"
                details={
                  <>
                    <p
                      css={css`
                        width: 125px;
                      `}
                    >
                      Press <b>q</b> to pause the demo
                    </p>
                  </>
                }
              />
              <Control
                keys={
                  <>
                    <div
                      css={css`
                        text-align: center;
                      `}
                    >
                      <KeyboardKey keyboardKey="W" />
                    </div>
                    <div>
                      <KeyboardKey keyboardKey="A" />
                      <KeyboardKey keyboardKey="S" />
                      <KeyboardKey keyboardKey="D" />
                    </div>
                  </>
                }
                action="Walk"
                details={
                  <>
                    <b>W</b>: Ahead
                    <br />
                    <b>A</b>: Left
                    <br />
                    <b>S</b>: Back
                    <br />
                    <b>D</b>: Right
                  </>
                }
              />
              <Control
                keys={
                  <>
                    <div
                      css={css`
                        text-align: center;
                      `}
                    >
                      <KeyboardKey keyboardKey="W" />
                    </div>
                    <div>
                      <KeyboardKey keyboardKey="A" />
                      <KeyboardKey keyboardKey="S" />
                      <KeyboardKey keyboardKey="D" />
                    </div>
                    <div
                      css={css`
                        margin-top: 5px;
                      `}
                    >
                      <KeyboardKey
                        keyboardKey="Shift"
                        width={2}
                        isPressed={true}
                      />
                    </div>
                  </>
                }
                action="Run"
                details={
                  <>
                    <b>Shift + W</b>: Ahead
                    <br />
                    <b>Shift + A</b>: Left
                    <br />
                    <b>Shift + S</b>: Back
                    <br />
                    <b>Shift + D</b>: Right
                  </>
                }
              />
              <Control
                keys={
                  <>
                    <div
                      css={css`
                        text-align: center;
                        width: 150px;
                      `}
                    >
                      <img
                        src={ROTATE_MOUSE_ICON}
                        css={css`
                          width: 100px;
                        `}
                      />
                    </div>
                  </>
                }
                action="Look Around"
                details={
                  <>
                    <p
                      css={css`
                        width: 150px;
                      `}
                    >
                      <b>Move the Mouse</b> to look around the scene
                    </p>
                  </>
                }
              />
              <Control
                keys={
                  <>
                    <div
                      css={css`
                        text-align: center;
                        width: 150px;
                      `}
                    >
                      <img
                        src={LEFT_CLICK_ICON}
                        css={css`
                          width: 75px;
                        `}
                      />
                    </div>
                  </>
                }
                action="Interact with Object"
                details={
                  <>
                    <p
                      css={css`
                        width: 150px;
                      `}
                    >
                      <b>Left Click</b> on an object close to the agent to
                      interact with it
                    </p>
                  </>
                }
              />
            </Section>
            {includeiTHOR ? (
              <Section sectionTitle="iTHOR" emoji="frog">
                <p
                  css={css`
                    max-width: 500px;
                  `}
                >
                  iTHOR includes 120 interactive scenes, evenly split between
                  kitchens, living rooms, bedrooms, and bathrooms. It includes
                  thousands of unique objects that have visual state changes
                  such as open/close, on/off, and hot/cold. As an introductory
                  example, try turning off the lights in FloorPlan1!
                </p>
                {includeKitchens ? (
                  <SubSection sectionTitle="Kitchens" emoji="cooking">
                    <Scenes
                      data={data}
                      scenes={kitchens}
                      onClick={sceneClick}
                      selectedScene={scene}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
                {includeLivingRooms ? (
                  <SubSection
                    sectionTitle="Living Rooms"
                    emoji="couch_and_lamp"
                  >
                    <Scenes
                      data={data}
                      scenes={living_rooms}
                      onClick={sceneClick}
                      selectedScene={scene}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
                {includeBedrooms ? (
                  <SubSection sectionTitle="Bedrooms" emoji="bed">
                    <Scenes
                      data={data}
                      scenes={bedrooms}
                      onClick={sceneClick}
                      selectedScene={scene}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
                {includeBathrooms ? (
                  <SubSection sectionTitle="bathrooms" emoji="toilet">
                    <Scenes
                      data={data}
                      scenes={bathrooms}
                      selectedScene={scene}
                      onClick={sceneClick}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
              </Section>
            ) : (
              <></>
            )}
            {includeRoboTHOR ? (
              <Section sectionTitle="RoboTHOR" emoji="wolf">
                <p
                  css={css`
                    max-width: 500px;
                  `}
                >
                  RoboTHOR includes 89 apartments, distributed as 60 training
                  scenes, 15 validation scenes, and 14 testing scenes (which
                  also exist in the real world). Scenes are numbered by{" "}
                  <i>WallConfiguration_i</i>, where scenes that share the same
                  first number share the same wall layout. The goal of RoboTHOR
                  is to study sim2real transfer.
                </p>
                {includeRoboTHORTrain ? (
                  <SubSection sectionTitle="Training" emoji="bullettrain_side">
                    <Scenes
                      data={data}
                      scenes={robothor_train}
                      onClick={sceneClick}
                      selectedScene={scene}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
                {includeRoboTHORVal ? (
                  <SubSection sectionTitle="Validation" emoji="teacher">
                    <Scenes
                      data={data}
                      scenes={robothor_val}
                      onClick={sceneClick}
                      selectedScene={scene}
                    />
                  </SubSection>
                ) : (
                  <></>
                )}
              </Section>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export const query = graphql`
  fragment SceneImage on File {
    childImageSharp {
      fluid(quality: 100) {
        ...GatsbyImageSharpFluid
      }
    }
  }

  query {
    FloorPlan1_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan2_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan3_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan4_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan5_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan6_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan6.png" }
    ) {
      ...SceneImage
    }
    FloorPlan7_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan7.png" }
    ) {
      ...SceneImage
    }
    FloorPlan8_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan8.png" }
    ) {
      ...SceneImage
    }
    FloorPlan9_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan9.png" }
    ) {
      ...SceneImage
    }
    FloorPlan10_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan10.png" }
    ) {
      ...SceneImage
    }
    FloorPlan11_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan11.png" }
    ) {
      ...SceneImage
    }
    FloorPlan12_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan12.png" }
    ) {
      ...SceneImage
    }
    FloorPlan13_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan13.png" }
    ) {
      ...SceneImage
    }
    FloorPlan14_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan14.png" }
    ) {
      ...SceneImage
    }
    FloorPlan15_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan15.png" }
    ) {
      ...SceneImage
    }
    FloorPlan16_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan16.png" }
    ) {
      ...SceneImage
    }
    FloorPlan17_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan17.png" }
    ) {
      ...SceneImage
    }
    FloorPlan18_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan18.png" }
    ) {
      ...SceneImage
    }
    FloorPlan19_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan19.png" }
    ) {
      ...SceneImage
    }
    FloorPlan20_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan20.png" }
    ) {
      ...SceneImage
    }
    FloorPlan21_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan21.png" }
    ) {
      ...SceneImage
    }
    FloorPlan22_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan22.png" }
    ) {
      ...SceneImage
    }
    FloorPlan23_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan23.png" }
    ) {
      ...SceneImage
    }
    FloorPlan24_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan24.png" }
    ) {
      ...SceneImage
    }
    FloorPlan25_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan25.png" }
    ) {
      ...SceneImage
    }
    FloorPlan26_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan26.png" }
    ) {
      ...SceneImage
    }
    FloorPlan27_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan27.png" }
    ) {
      ...SceneImage
    }
    FloorPlan28_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan28.png" }
    ) {
      ...SceneImage
    }
    FloorPlan29_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan29.png" }
    ) {
      ...SceneImage
    }
    FloorPlan30_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan30.png" }
    ) {
      ...SceneImage
    }
    FloorPlan201_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan201.png" }
    ) {
      ...SceneImage
    }
    FloorPlan202_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan202.png" }
    ) {
      ...SceneImage
    }
    FloorPlan203_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan203.png" }
    ) {
      ...SceneImage
    }
    FloorPlan204_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan204.png" }
    ) {
      ...SceneImage
    }
    FloorPlan205_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan205.png" }
    ) {
      ...SceneImage
    }
    FloorPlan206_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan206.png" }
    ) {
      ...SceneImage
    }
    FloorPlan207_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan207.png" }
    ) {
      ...SceneImage
    }
    FloorPlan208_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan208.png" }
    ) {
      ...SceneImage
    }
    FloorPlan209_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan209.png" }
    ) {
      ...SceneImage
    }
    FloorPlan210_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan210.png" }
    ) {
      ...SceneImage
    }
    FloorPlan211_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan211.png" }
    ) {
      ...SceneImage
    }
    FloorPlan212_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan212.png" }
    ) {
      ...SceneImage
    }
    FloorPlan213_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan213.png" }
    ) {
      ...SceneImage
    }
    FloorPlan214_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan214.png" }
    ) {
      ...SceneImage
    }
    FloorPlan215_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan215.png" }
    ) {
      ...SceneImage
    }
    FloorPlan216_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan216.png" }
    ) {
      ...SceneImage
    }
    FloorPlan217_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan217.png" }
    ) {
      ...SceneImage
    }
    FloorPlan218_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan218.png" }
    ) {
      ...SceneImage
    }
    FloorPlan219_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan219.png" }
    ) {
      ...SceneImage
    }
    FloorPlan220_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan220.png" }
    ) {
      ...SceneImage
    }
    FloorPlan221_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan221.png" }
    ) {
      ...SceneImage
    }
    FloorPlan222_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan222.png" }
    ) {
      ...SceneImage
    }
    FloorPlan223_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan223.png" }
    ) {
      ...SceneImage
    }
    FloorPlan224_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan224.png" }
    ) {
      ...SceneImage
    }
    FloorPlan225_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan225.png" }
    ) {
      ...SceneImage
    }
    FloorPlan226_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan226.png" }
    ) {
      ...SceneImage
    }
    FloorPlan227_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan227.png" }
    ) {
      ...SceneImage
    }
    FloorPlan228_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan228.png" }
    ) {
      ...SceneImage
    }
    FloorPlan229_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan229.png" }
    ) {
      ...SceneImage
    }
    FloorPlan230_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan230.png" }
    ) {
      ...SceneImage
    }
    FloorPlan301_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan301.png" }
    ) {
      ...SceneImage
    }
    FloorPlan302_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan302.png" }
    ) {
      ...SceneImage
    }
    FloorPlan303_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan303.png" }
    ) {
      ...SceneImage
    }
    FloorPlan304_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan304.png" }
    ) {
      ...SceneImage
    }
    FloorPlan305_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan305.png" }
    ) {
      ...SceneImage
    }
    FloorPlan306_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan306.png" }
    ) {
      ...SceneImage
    }
    FloorPlan307_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan307.png" }
    ) {
      ...SceneImage
    }
    FloorPlan308_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan308.png" }
    ) {
      ...SceneImage
    }
    FloorPlan309_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan309.png" }
    ) {
      ...SceneImage
    }
    FloorPlan310_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan310.png" }
    ) {
      ...SceneImage
    }
    FloorPlan311_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan311.png" }
    ) {
      ...SceneImage
    }
    FloorPlan312_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan312.png" }
    ) {
      ...SceneImage
    }
    FloorPlan313_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan313.png" }
    ) {
      ...SceneImage
    }
    FloorPlan314_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan314.png" }
    ) {
      ...SceneImage
    }
    FloorPlan315_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan315.png" }
    ) {
      ...SceneImage
    }
    FloorPlan316_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan316.png" }
    ) {
      ...SceneImage
    }
    FloorPlan317_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan317.png" }
    ) {
      ...SceneImage
    }
    FloorPlan318_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan318.png" }
    ) {
      ...SceneImage
    }
    FloorPlan319_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan319.png" }
    ) {
      ...SceneImage
    }
    FloorPlan320_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan320.png" }
    ) {
      ...SceneImage
    }
    FloorPlan321_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan321.png" }
    ) {
      ...SceneImage
    }
    FloorPlan322_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan322.png" }
    ) {
      ...SceneImage
    }
    FloorPlan323_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan323.png" }
    ) {
      ...SceneImage
    }
    FloorPlan324_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan324.png" }
    ) {
      ...SceneImage
    }
    FloorPlan325_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan325.png" }
    ) {
      ...SceneImage
    }
    FloorPlan326_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan326.png" }
    ) {
      ...SceneImage
    }
    FloorPlan327_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan327.png" }
    ) {
      ...SceneImage
    }
    FloorPlan328_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan328.png" }
    ) {
      ...SceneImage
    }
    FloorPlan329_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan329.png" }
    ) {
      ...SceneImage
    }
    FloorPlan330_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan330.png" }
    ) {
      ...SceneImage
    }
    FloorPlan401_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan401.png" }
    ) {
      ...SceneImage
    }
    FloorPlan402_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan402.png" }
    ) {
      ...SceneImage
    }
    FloorPlan403_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan403.png" }
    ) {
      ...SceneImage
    }
    FloorPlan404_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan404.png" }
    ) {
      ...SceneImage
    }
    FloorPlan405_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan405.png" }
    ) {
      ...SceneImage
    }
    FloorPlan406_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan406.png" }
    ) {
      ...SceneImage
    }
    FloorPlan407_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan407.png" }
    ) {
      ...SceneImage
    }
    FloorPlan408_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan408.png" }
    ) {
      ...SceneImage
    }
    FloorPlan409_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan409.png" }
    ) {
      ...SceneImage
    }
    FloorPlan410_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan410.png" }
    ) {
      ...SceneImage
    }
    FloorPlan411_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan411.png" }
    ) {
      ...SceneImage
    }
    FloorPlan412_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan412.png" }
    ) {
      ...SceneImage
    }
    FloorPlan413_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan413.png" }
    ) {
      ...SceneImage
    }
    FloorPlan414_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan414.png" }
    ) {
      ...SceneImage
    }
    FloorPlan415_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan415.png" }
    ) {
      ...SceneImage
    }
    FloorPlan416_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan416.png" }
    ) {
      ...SceneImage
    }
    FloorPlan417_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan417.png" }
    ) {
      ...SceneImage
    }
    FloorPlan418_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan418.png" }
    ) {
      ...SceneImage
    }
    FloorPlan419_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan419.png" }
    ) {
      ...SceneImage
    }
    FloorPlan420_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan420.png" }
    ) {
      ...SceneImage
    }
    FloorPlan421_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan421.png" }
    ) {
      ...SceneImage
    }
    FloorPlan422_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan422.png" }
    ) {
      ...SceneImage
    }
    FloorPlan423_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan423.png" }
    ) {
      ...SceneImage
    }
    FloorPlan424_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan424.png" }
    ) {
      ...SceneImage
    }
    FloorPlan425_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan425.png" }
    ) {
      ...SceneImage
    }
    FloorPlan426_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan426.png" }
    ) {
      ...SceneImage
    }
    FloorPlan427_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan427.png" }
    ) {
      ...SceneImage
    }
    FloorPlan428_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan428.png" }
    ) {
      ...SceneImage
    }
    FloorPlan429_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan429.png" }
    ) {
      ...SceneImage
    }
    FloorPlan430_physics: file(
      relativePath: { eq: "scenes/small/FloorPlan430.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train1_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train1_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train1_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train1_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train1_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train1_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train1_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train1_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train1_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train1_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train2_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train2_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train2_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train2_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train2_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train2_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train2_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train2_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train2_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train2_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train3_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train3_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train3_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train3_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train3_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train3_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train3_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train3_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train3_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train3_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train4_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train4_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train4_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train4_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train4_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train4_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train4_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train4_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train4_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train4_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train5_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train5_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train5_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train5_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train5_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train5_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train5_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train5_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train5_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train5_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train6_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train6_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train6_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train6_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train6_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train6_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train6_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train6_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train6_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train6_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train7_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train7_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train7_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train7_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train7_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train7_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train7_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train7_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train7_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train7_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train8_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train8_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train8_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train8_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train8_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train8_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train8_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train8_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train8_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train8_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train9_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train9_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train9_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train9_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train9_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train9_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train9_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train9_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train9_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train9_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train10_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train10_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train10_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train10_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train10_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train10_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train10_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train10_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train10_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train10_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train11_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train11_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train11_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train11_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train11_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train11_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train11_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train11_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train11_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train11_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train12_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train12_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train12_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train12_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train12_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train12_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train12_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train12_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Train12_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Train12_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val1_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val1_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val1_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val1_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val1_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val1_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val1_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val1_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val1_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val1_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val2_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val2_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val2_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val2_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val2_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val2_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val2_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val2_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val2_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val2_5.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val3_1: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val3_1.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val3_2: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val3_2.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val3_3: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val3_3.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val3_4: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val3_4.png" }
    ) {
      ...SceneImage
    }
    FloorPlan_Val3_5: file(
      relativePath: { eq: "scenes/small/FloorPlan_Val3_5.png" }
    ) {
      ...SceneImage
    }
  }
`;
